import { ReactNode } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import RepeatOutlinedIcon from '@mui/icons-material/RepeatOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import { FlashDesignWithRelations } from '../../types/flashbooks';
import {
  FLASH_DESIGN_DEPOSIT_OPTIONS,
  FLASH_DESIGN_SIZE_OPTIONS,
} from '../../constants/flashbooks';
import {
  detailRowIconStyle,
  detailRowLastStyle,
  detailRowStyle,
  flashbookCardStyle,
  tagChipStyle,
} from '../../helpers/styles/flashbook';

interface Props {
  design: FlashDesignWithRelations;
}

const formatSizesTitle = (design: FlashDesignWithRelations) => {
  const enabled = design.pricing.filter(p => p.enabled);
  if (!enabled.length) return 'No sizes available';
  const initials = enabled
    .map(p => {
      const meta = FLASH_DESIGN_SIZE_OPTIONS.find(o => o.value === p.size);
      return meta?.label?.[0]?.toUpperCase() ?? '';
    })
    .filter(Boolean)
    .join(', ');
  return `${enabled.length} size${
    enabled.length === 1 ? '' : 's'
  } available (${initials})`;
};

const formatSizesBody = (design: FlashDesignWithRelations) => {
  const enabled = design.pricing.filter(p => p.enabled);
  if (!enabled.length) return null;
  return enabled
    .map(p => {
      const meta = FLASH_DESIGN_SIZE_OPTIONS.find(o => o.value === p.size);
      const hours = p.estimatedDurationInMinutes / 60;
      const hr =
        hours > 0 ? `${hours % 1 === 0 ? hours : hours.toFixed(1)} hr` : null;
      return [meta?.label, hr, p.price > 0 ? `$${p.price}` : null]
        .filter(Boolean)
        .join(' · ');
    })
    .join('  ·  ');
};

const formatStartsAt = (design: FlashDesignWithRelations) => {
  const enabled = design.pricing.filter(p => p.enabled && p.price > 0);
  if (!enabled.length) return 'No estimate';
  return `Starts at $${Math.min(...enabled.map(p => p.price))}`;
};

const formatPaymentRule = (design: FlashDesignWithRelations) => {
  const option = FLASH_DESIGN_DEPOSIT_OPTIONS.find(
    o => o.value === design.depositBehavior,
  );
  if (!option) return null;
  if (design.depositBehavior === 'NONE') {
    return 'No deposit required — clients can book directly.';
  }
  if (design.depositBehavior === 'FLAT_FEE') {
    return `Flat deposit of $${design.depositAmount} collected at booking.`;
  }
  if (design.depositBehavior === 'PERCENTAGE') {
    return `${Math.round(
      design.depositPercentage * 100,
    )}% of the estimate collected at booking.`;
  }
  return option.label;
};

interface RowProps {
  icon: ReactNode;
  title: string;
  body?: ReactNode;
  isLast?: boolean;
}

function Row({ icon, title, body, isLast }: RowProps) {
  return (
    <Stack
      direction="row"
      gap={1.75}
      alignItems="flex-start"
      sx={isLast ? detailRowLastStyle : detailRowStyle}>
      <Box sx={detailRowIconStyle}>{icon}</Box>
      <Box flex={1} minWidth={0}>
        <Typography
          variant="h4"
          color="common.black"
          lineHeight={1.2}
          mb={body ? 0.75 : 0}>
          {title}
        </Typography>
        {body}
      </Box>
    </Stack>
  );
}

export default function FlashDesignDetailCard({ design }: Props) {
  const sizesBody = formatSizesBody(design);
  const paymentBody = formatPaymentRule(design);

  return (
    <Box sx={flashbookCardStyle}>
      <Row
        icon={<PaletteOutlinedIcon />}
        title={design.name}
        body={
          <Stack gap={1.25}>
            {design.description ? (
              <Typography
                variant="body2"
                color="text.primary"
                lineHeight={1.55}>
                {design.description}
              </Typography>
            ) : null}
            {design.tags?.length ? (
              <Stack direction="row" gap={0.75} flexWrap="wrap">
                {design.tags.map(tag => (
                  <Chip key={tag} label={tag} size="small" sx={tagChipStyle} />
                ))}
              </Stack>
            ) : null}
          </Stack>
        }
      />
      <Row
        icon={<OpenInFullOutlinedIcon />}
        title={formatSizesTitle(design)}
        body={
          sizesBody ? (
            <Typography variant="body2" color="text.primary" lineHeight={1.55}>
              {sizesBody}
            </Typography>
          ) : null
        }
      />
      <Row
        icon={<RepeatOutlinedIcon />}
        title={design.isRepeatable ? 'Repeatable' : 'One of one'}
        body={
          <Typography variant="body2" color="text.primary" lineHeight={1.55}>
            {design.isRepeatable
              ? 'Clients can book this same design more than once.'
              : 'Only one client can book this design.'}
          </Typography>
        }
      />
      <Row
        isLast
        icon={<AttachMoneyOutlinedIcon />}
        title={formatStartsAt(design)}
        body={
          paymentBody ? (
            <Typography variant="body2" color="text.primary" lineHeight={1.55}>
              {paymentBody}
            </Typography>
          ) : null
        }
      />
    </Box>
  );
}
